/*
 * <lazy-loop-video> — a muted, looping video over a photo. Markup: snippets/lazy-loop-video.liquid.
 *
 * Downloads nothing until the page has loaded AND the element is within 200px of the
 * viewport, then exactly one file, chosen by breakpoint (`media` on <source> is ignored inside <video>, so the
 * markup cannot choose). Pauses whenever it leaves the screen — including when a parent
 * becomes display:none, e.g. the homepage takeover's inactive group.
 *
 * Does nothing at all — the photo underneath simply stays — under reduced motion,
 * Save-Data, without IntersectionObserver, or when play() is refused (iOS Low Power Mode).
 */

if (!customElements.get('lazy-loop-video')) {
  customElements.define(
    'lazy-loop-video',
    class LazyLoopVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || this.observer) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (navigator.connection && navigator.connection.saveData) return;
        if (!('IntersectionObserver' in window)) return;

        // iOS only allows inline autoplay when the PROPERTY is set, not just the attribute.
        this.video.muted = true;
        this.video.addEventListener('playing', () => this.classList.add('is-playing'));

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.play();
              } else {
                this.video.pause();
              }
            });
          },
          { rootMargin: '200px 0px' }
        );

        // Not before the page's own load: a loop sitting near the fold must never
        // compete with the hero image, and the layout is final by then.
        if (document.readyState === 'complete') {
          this.observer.observe(this);
        } else {
          window.addEventListener('load', () => this.observer && this.observer.observe(this), { once: true });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
      }

      play() {
        if (!this.video.getAttribute('src')) {
          const desktop = window.matchMedia('(min-width: 750px)').matches;
          const src = desktop ? this.dataset.src : this.dataset.srcMobile;
          // No URL for this breakpoint (the slot is hidden on phones): never fetch.
          if (!src) return;
          this.video.setAttribute('src', src);
        }

        const playing = this.video.play();
        if (playing && playing.catch) playing.catch(() => {});
      }
    }
  );
}
